import * as React from 'react'
import {ReactNode, FC, useState} from 'react'
import {observer} from 'mobx-react'
import {Transition} from 'react-transition-group'
import IMask from 'imask'
import {IMaskInput} from 'react-imask'
import cn from 'classnames'
import {isFunction} from 'lodash'
import {buildSlideTransitionProps} from '../../lib/helpers'
import {useStores} from '../../lib/MobXProvider'
import {WidgetApi} from '../../lib/WidgetApi'
import {SlotComponentConfig} from '../../lib/WidgetConfig'
import {DynamicComponent} from './DynamicComponent'
import {PaymentForm} from '../../lib/forms/PaymentForm'
import {SubscriptionRenewForm} from '../../lib/forms/SubscriptionRenewForm'
import {
  InputCheckboxWrapper,
  InputRadioWrapper,
} from '../wrappers/InputCheckableWrapper'
import {InputWrapper} from '../wrappers/InputWrapper'
import {DirtyWrapper} from '../wrappers/DirtyWrapper'
import {SelectWrapper} from '../wrappers/SelectWrapper'
import {RippleWrapper} from '../wrappers/RippleWrapper'
import {LabelMarkRequiredWrapper} from '../wrappers/LabelMarkRequiredWrapper'
import {TextareaWrapper} from '../wrappers/TextareaWrapper'
import {FormPart} from '../wrappers/FormPart'
import {InfoToggle} from '../ui/InfoToggle'
import {ErrorMessage} from '../ui/ErrorMessage'
import {IconInfo} from '../ui/Icons'

let moment = require('moment')

///////////////////////////////////////////////////////////////////////////////

export type DynamicFieldType =
  | 'text'
  | 'textarea'
  | 'date'
  | 'radio-group'
  | 'radio-in-group'
  | 'checkbox'
  | 'checkbox-group'
  | 'checkbox-in-group'
  | 'select'

export type DynamicFieldForm = 'payment_form' | 'subscription_renew_form'

export type SelectOption = {
  value: string
  label: string
}

export type DynamicFieldProps = {
  type: DynamicFieldType
  form: PaymentForm | SubscriptionRenewForm
  formName?: DynamicFieldForm
  label?: string
  name?: string
  text?: string | ReactNode | (() => ReactNode)
  textHTML?: string | ReactNode | (() => ReactNode)
  value?: any
  infoFixedHTML?: string
  infoExpandableHTML?: string
  placeholder?: string
  infoToggleTitle?: string
  infoToggleTextHTML?: string
  inline?: boolean
  options?: SelectOption[]
  children?: ReactNode | SlotComponentConfig[]
  readForm?: () => any
  writeForm?: (value) => void
  compareForm?: (value) => boolean
}

///////////////////////////////////////////////////////////////////////////////

export const DynamicField: FC<DynamicFieldProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})

  let {
    type,
    form,
    formName,
    label,
    name,
    text,
    textHTML,
    value,
    infoFixedHTML,
    infoExpandableHTML,
    placeholder,
    infoToggleTitle,
    infoToggleTextHTML,
    inline,
    options,
    children,
    readForm,
    writeForm,
    compareForm,
  } = props

  if (!form) {
    if (formName === 'payment_form') {
      form = api.paymentForm
    } else if (formName === 'subscription_renew_form') {
      form = api.subscriptionRenewForm
    } else {
      if (formName === null || formName === undefined) {
        throw new Error(`Missing form name for custom field "${name}"`)
      } else {
        throw new Error(
          `Invalid form name "${formName}" for custom field "${name}"`,
        )
      }
    }
  }

  let errors = form.validationErrors

  readForm = readForm || (() => form.data[name])
  writeForm = writeForm || ((v) => (form.data[name] = v))
  compareForm = compareForm || ((v) => readForm() === v)

  let labelTranslated = label ? api.trans(label) : null

  let placeholderTranslated = placeholder
    ? api.trans(placeholder, {defaultValue: null})
    : null

  let textTranslated = isFunction(text)
    ? text()
    : text
    ? api.trans(text as string)
    : null

  let textHTMLTranslated = isFunction(textHTML)
    ? textHTML()
    : textHTML
    ? api.trans(textHTML as string)
    : null

  let infoFixedHTMLTranslated = infoFixedHTML
    ? api.trans(infoFixedHTML, {defaultValue: null})
    : null

  let infoExpandableHTMLTranslated = infoExpandableHTML
    ? api.trans(infoExpandableHTML, {defaultValue: null})
    : null

  children = (() => {
    if (
      children &&
      children[0] !== undefined &&
      (children as SlotComponentConfig[])[0].component !== undefined
    ) {
      return (
        <DynamicComponent
          slots={children as SlotComponentConfig[]}
          form={form}
        />
      )
    } else {
      return children
    }
  })()

  let submit = () => form.validateAndSend()

  let infoToggleTitleTranslated = infoToggleTitle
    ? api.trans(infoToggleTitle, {defaultValue: null})
    : null

  let infoToggleTextHTMLTranslated = infoToggleTextHTML
    ? api.trans(infoToggleTextHTML, {defaultValue: null})
    : null

  let [infoToggleExpanded, setInfoToggleExpanded] = useState(false)

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'text') {
    value = readForm() || ''

    return (
      <FormPart
        className={cn('form-block', name, {
          'has-info-button': infoToggleTextHTMLTranslated,
        })}
        onSubmit={submit}
      >
        <div className="field-block">
          {label && (
            <label
              className="label-block"
              htmlFor={api.ui.generateUniqueElementId(name)}
            >
              {labelTranslated}
            </label>
          )}

          <InputWrapper form={form} fields={name}>
            <DirtyWrapper form={form} fields={name}>
              <input
                type="text"
                id={api.ui.generateUniqueElementId(name)}
                aria-describedby={api.ui.generateUniqueElementId(
                  name + '-error',
                )}
                className="input-text"
                value={value}
                name={name}
                onChange={(e) => writeForm(e.target.value)}
                placeholder={placeholderTranslated}
              />
            </DirtyWrapper>
          </InputWrapper>

          <div className="icons-block">
            {infoToggleTextHTMLTranslated && (
              <button
                type="button"
                aria-label={infoToggleTitleTranslated}
                onClick={() => setInfoToggleExpanded((v) => !v)}
                aria-expanded={infoToggleExpanded}
                aria-controls={api.ui.generateUniqueElementId(
                  `info-toggle-${name}`,
                )}
                className={cn('info-button', {
                  expanded: infoToggleExpanded,
                  collapsed: !infoToggleExpanded,
                })}
              >
                <span className="icon">
                  <IconInfo />
                </span>
              </button>
            )}
          </div>
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />

        <InfoToggle
          idKey={`info-toggle-${name}`}
          className={`info-toggle-${name}`}
          textHTML={infoToggleTextHTMLTranslated}
          expanded={infoToggleExpanded}
        />
      </FormPart>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'textarea') {
    value = readForm() || ''

    return (
      <div
        className={cn('form-block', name, {
          'has-info-button': infoToggleTextHTMLTranslated,
        })}
      >
        <div className="field-block">
          {label && (
            <label
              className="label-block"
              htmlFor={api.ui.generateUniqueElementId(name)}
            >
              {labelTranslated}
            </label>
          )}

          <InputWrapper form={form} fields={name}>
            <DirtyWrapper form={form} fields={name}>
              <TextareaWrapper>
                <textarea
                  id={api.ui.generateUniqueElementId(name)}
                  aria-describedby={api.ui.generateUniqueElementId(
                    name + '-error',
                  )}
                  className="input-textarea"
                  value={value}
                  name={name}
                  onChange={(e) => writeForm(e.target.value)}
                  rows={1}
                  placeholder={placeholderTranslated}
                />
              </TextareaWrapper>
            </DirtyWrapper>
          </InputWrapper>

          <div className="icons-block">
            {infoToggleTextHTMLTranslated && (
              <button
                type="button"
                aria-label={infoToggleTitleTranslated}
                onClick={() => setInfoToggleExpanded((v) => !v)}
                aria-expanded={infoToggleExpanded}
                aria-controls={api.ui.generateUniqueElementId(
                  `info-toggle-${name}`,
                )}
                className={cn('info-button', {
                  expanded: infoToggleExpanded,
                  collapsed: !infoToggleExpanded,
                })}
              >
                <span className="icon">
                  <IconInfo />
                </span>
              </button>
            )}
          </div>
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />

        <InfoToggle
          idKey={`info-toggle-${name}`}
          className={`info-toggle-${name}`}
          textHTML={infoToggleTextHTMLTranslated}
          expanded={infoToggleExpanded}
        />
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'date') {
    value = readForm() || ''

    let {dateStoreFormat, dateDisplayFormat} = api.computedConfig
    let dateDisplayFormatTranslated = api.trans('date_display_format', {
      defaultValue: dateDisplayFormat,
    })
    let valueDate = moment(value, dateStoreFormat, true)

    if (valueDate.isValid()) {
      value = valueDate.format(dateDisplayFormat)
    }

    let dateMaskProps = {
      mask: dateDisplayFormat,
      blocks: {
        YYYY: {
          mask: IMask.MaskedRange,
          from: 1900,
          to: 2100,
          maxLength: 4,
        },
        MM: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
          maxLength: 2,
        },
        DD: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
          maxLength: 2,
        },
      },
      onAccept: (value, mask) => {
        let date = moment(value, dateDisplayFormat, true)
        let v = value

        if (date.isValid()) {
          v = date.format(dateStoreFormat)
        }

        writeForm(v)
      },
    }

    return (
      <FormPart
        className={cn('form-block', name, {
          'has-info-button': infoToggleTextHTMLTranslated,
        })}
        onSubmit={submit}
      >
        <div className="field-block">
          {label && (
            <label
              className="label-block"
              htmlFor={api.ui.generateUniqueElementId(name)}
            >
              {labelTranslated}
            </label>
          )}

          <InputWrapper form={form} fields={name}>
            <DirtyWrapper form={form} fields={name}>
              <IMaskInput
                {...dateMaskProps}
                type="text"
                inputMode="numeric"
                data-pattern="[0-9]*"
                id={api.ui.generateUniqueElementId(name)}
                aria-describedby={api.ui.generateUniqueElementId(
                  name + '-error',
                )}
                className="input-text"
                name={name}
                placeholder={dateDisplayFormatTranslated}
                value={value}
              />
            </DirtyWrapper>
          </InputWrapper>

          <div className="icons-block">
            {infoToggleTextHTMLTranslated && (
              <button
                type="button"
                aria-label={infoToggleTitleTranslated}
                onClick={() => setInfoToggleExpanded((v) => !v)}
                aria-expanded={infoToggleExpanded}
                aria-controls={api.ui.generateUniqueElementId(
                  `info-toggle-${name}`,
                )}
                className={cn('info-button', {
                  expanded: infoToggleExpanded,
                  collapsed: !infoToggleExpanded,
                })}
              >
                <span className="icon">
                  <IconInfo />
                </span>
              </button>
            )}
          </div>
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />

        <InfoToggle
          idKey={`info-toggle-${name}`}
          className={`info-toggle-${name}`}
          textHTML={infoToggleTextHTMLTranslated}
          expanded={infoToggleExpanded}
        />
      </FormPart>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'radio-group') {
    return (
      <div className={cn('form-block', name)}>
        {label && (
          <LabelMarkRequiredWrapper form={form} fields={name}>
            <div className="label-block">{labelTranslated}</div>
          </LabelMarkRequiredWrapper>
        )}

        <div className={cn(['fields-block', {'fields-block-inline': inline}])}>
          {children}
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'radio-in-group') {
    value = value || ''
    let checked = compareForm(value)
    let errorId = api.ui.generateUniqueElementId(name + '-error')

    return (
      <div className={cn('field-block')}>
        <RippleWrapper>
          <label className="label-radio-block">
            <InputRadioWrapper form={form} fields={name} skipMarkRequired>
              <DirtyWrapper form={form} fields={name}>
                <input
                  type="radio"
                  aria-describedby={errorId}
                  className="input-radio"
                  name={name}
                  value={value}
                  checked={checked}
                  onChange={(e) => writeForm(e.target.value)}
                />
              </DirtyWrapper>
            </InputRadioWrapper>

            {textHTMLTranslated && (
              <div
                style={{zIndex: 1}}
                dangerouslySetInnerHTML={{__html: textHTMLTranslated}}
              />
            )}
            {textTranslated && !textHTMLTranslated && (
              <span>{textTranslated}</span>
            )}
          </label>
        </RippleWrapper>
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'checkbox') {
    let checked = !!readForm()
    let errorId = api.ui.generateUniqueElementId(name + '-error')
    let infoId = api.ui.generateUniqueElementId(name + '-info')

    return (
      <div className={cn('form-block', name)}>
        <div className="field-block">
          {label && <div className="label-block">{labelTranslated}</div>}

          <RippleWrapper>
            <label className="label-checkbox-block">
              <InputCheckboxWrapper form={form} fields={name}>
                <DirtyWrapper form={form} fields={name}>
                  <input
                    type="checkbox"
                    aria-describedby={errorId + ' ' + infoId}
                    className="input-checkbox"
                    name={name}
                    checked={checked}
                    onChange={() => writeForm(!checked)}
                  />
                </DirtyWrapper>
              </InputCheckboxWrapper>

              {textHTMLTranslated && (
                <div
                  style={{zIndex: 1}}
                  dangerouslySetInnerHTML={{__html: textHTMLTranslated}}
                />
              )}
              {textTranslated && !textHTMLTranslated && (
                <span>{textTranslated}</span>
              )}
            </label>
          </RippleWrapper>

          {infoFixedHTMLTranslated && (
            <div className="footer">
              <div
                className="text"
                dangerouslySetInnerHTML={{__html: infoFixedHTMLTranslated}}
                id={infoId}
              />
            </div>
          )}

          {infoExpandableHTMLTranslated && !infoFixedHTMLTranslated && (
            <Transition
              in={checked}
              mountOnEnter
              unmountOnExit
              {...slideTransitionProps}
            >
              <div className="footer" aria-live="polite">
                <div
                  className="text"
                  dangerouslySetInnerHTML={{
                    __html: infoExpandableHTMLTranslated,
                  }}
                  id={infoId}
                />
              </div>
            </Transition>
          )}
        </div>

        <ErrorMessage errors={errors} errorKey={name} id={errorId} />
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'checkbox-group') {
    return (
      <div className={cn('form-block', name)}>
        {label && (
          <LabelMarkRequiredWrapper form={form} fields={name}>
            <div className="label-block">{labelTranslated}</div>
          </LabelMarkRequiredWrapper>
        )}

        <div className={cn(['fields-block', {'fields-block-inline': inline}])}>
          {children}
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'checkbox-in-group') {
    let checked = !!readForm()
    let errorId = api.ui.generateUniqueElementId(name + '-error')
    let infoId = api.ui.generateUniqueElementId(name + '-info')

    return (
      <div className={cn('field-block')}>
        <RippleWrapper>
          <label className="label-checkbox-block">
            <InputCheckboxWrapper form={form} fields={name} skipMarkRequired>
              <DirtyWrapper form={form} fields={name}>
                <input
                  type="checkbox"
                  aria-describedby={errorId + ' ' + infoId}
                  className="input-checkbox"
                  name={name}
                  checked={checked}
                  onChange={() => writeForm(!checked)}
                />
              </DirtyWrapper>
            </InputCheckboxWrapper>

            {textHTMLTranslated && (
              <div
                style={{zIndex: 1}}
                dangerouslySetInnerHTML={{__html: textHTMLTranslated}}
              />
            )}
            {textTranslated && !textHTMLTranslated && (
              <span>{textTranslated}</span>
            )}
          </label>
        </RippleWrapper>

        {infoFixedHTMLTranslated && (
          <div className="footer">
            <div
              className="text"
              dangerouslySetInnerHTML={{__html: infoFixedHTMLTranslated}}
              id={infoId}
            />
          </div>
        )}

        {infoExpandableHTMLTranslated && !infoFixedHTMLTranslated && (
          <Transition
            in={checked}
            mountOnEnter
            unmountOnExit
            {...slideTransitionProps}
          >
            <div className="footer" aria-live="polite">
              <div
                className="text"
                dangerouslySetInnerHTML={{
                  __html: infoExpandableHTMLTranslated,
                }}
                id={infoId}
              />
            </div>
          </Transition>
        )}
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === 'select') {
    value = readForm() || ''

    return (
      <div className={cn('form-block', name)}>
        <div className="field-block">
          {label && (
            <label
              className="label-block"
              htmlFor={api.ui.generateUniqueElementId(name)}
            >
              {labelTranslated}
            </label>
          )}

          <InputWrapper form={form} fields={name}>
            <DirtyWrapper form={form} fields={name}>
              <SelectWrapper>
                <select
                  className="input-select"
                  id={api.ui.generateUniqueElementId(name)}
                  aria-describedby={api.ui.generateUniqueElementId(
                    name + '-error',
                  )}
                  value={value}
                  name={name}
                  onChange={(e) => writeForm(e.target.value)}
                >
                  {label && (
                    <option value="" disabled={true}>
                      {labelTranslated}
                    </option>
                  )}

                  {options &&
                    options.map((option, key) => (
                      <option key={key} value={option.value}>
                        {api.trans(option.label)}
                      </option>
                    ))}
                </select>
              </SelectWrapper>
            </DirtyWrapper>
          </InputWrapper>
        </div>

        <ErrorMessage
          errors={errors}
          errorKey={name}
          id={api.ui.generateUniqueElementId(name + '-error')}
        />
      </div>
    )
  }

  //////////////////////////////////////////////////////////////////////////////

  if (type === null) {
    throw new Error('Missing field type for custom field')
  } else {
    throw new Error('Invalid field type "' + type + '" for custom field')
  }
})
